import { ReparoApi } from './index';
import type { RateBasis } from './inventory';

// ─── Shared types ─────────────────────────────────────────────────────────────

export type DistributionDemandSource = 'FORECAST' | 'PAST_SALES' | 'OPEN_PO' | 'MAX_OF_FORECAST_PAST_SALES';

/**
 * Per-channel email template + recipient book used when emailing a distribution plan.
 * Channel-agnostic: resolved from the plan's channel. See docs/prds/distribution-plan-email-v1.md.
 */
export interface DistributionEmailConfig {
    enabled: boolean;
    signature: string | null;
    replyTo: string | null;
    /** Placeholders: {source} {destination} {orderId} {skuCount} {qty} {measurement} {dispatchDate} {receivingDate} */
    subjectTemplate: string;
    introLine: string;
    instructionLines: string[];
    alwaysTo: string[];
    alwaysCc: string[];
    /** Keyed by destination warehouseId. */
    byDestination: Record<string, { to: string[]; cc: string[] }>;
    /** Keyed by source warehouseId. */
    bySourceWarehouse: Record<string, { shortCode: string; extraCc: string[]; to?: string[] }>;
}

export interface DistributionChannelConfig {
    id: string;
    channelAbbreviationKey: string;
    isAggregated: boolean;
    demandSource: DistributionDemandSource;
    /** Rate signal for PO-driven channels (FORECAST | PAST_SALES). Null for push channels. */
    velocitySource: DistributionDemandSource | null;
    targetDohDays: number | null;
    horizonDays: number | null;
    milestone1Days: number;
    milestone2Days: number;
    tierTargetDohDays: Record<string, number> | null;
    /** 0–1 fraction of fromWH stock that can be sent in one plan run. Null = no cap. */
    maxOutboundUtilizationPct: number | null;
    /** Horizon (days) of inbound POs added to fromWH available stock. 0 = on-hand only. */
    inboundInTransitDays: number;
    /** SKUs with fromWH stock below this threshold are excluded from the plan. Null = no minimum. */
    minSourceStockUnits: number | null;
    /** Soft reserve (units) kept back at each fromWH. Plan sends down to this level. Null = no reserve. */
    sourceReserveUnits: number | null;
    /** Internal days-on-hand floor (days). Caps total units leaving the network per SKU. Null = no floor. */
    minInternalDohDays: number | null;
    /** Max units per outbound shipment. Null = no cap. */
    maxUnitsPerShipment: number | null;
    /** Max org-unit measurement per shipment. Null = no cap. */
    maxMeasurementPerShipment: number | null;
    /** Measurement unit for maxMeasurementPerShipment. */
    measurementUnit: 'weight' | 'volume' | null;
    /** Per-channel distribution email template + recipients. Null = not configured. */
    emailConfig: DistributionEmailConfig | null;
    createdAt: string;
    updatedAt: string;
}

export interface WarehouseDohRule {
    id: string;
    warehouseId: string;
    targetDohDays: number | null;
    tierTargetDohDays: Record<string, number> | null;
}

export interface AvailableChannel {
    channelAbbreviationKey: string;
    channelName: string;
    config: DistributionChannelConfig | null;
    pathCount: number;
}

export interface DistributionPath {
    id: string;
    merchantId: string;
    channelAbbreviationKey: string;
    fromWarehouseId: string;
    toWarehouseId: string | null;
    leadTimeDays: number;
    leadTimeMode: DistributionLeadTimeMode;
    measuredLeadDays: number | null;
    measuredTotalDays: number | null;
    measuredSample: number | null;
    isActive: boolean;
    createdAt: string;
    updatedAt: string;
}

export type DistributionLeadTimeMode = 'AUTO' | 'MANUAL';

export type LeadTimeScope = 'PATH' | 'FC' | 'CHANNEL';

export interface MeasuredDays {
    days: number;
    sample: number;
    scope: LeadTimeScope;
    windowDays: number;
}

export interface LeadTimeMeasurement {
    lead: MeasuredDays | null;
    total: MeasuredDays | null;
    prep: MeasuredDays | null;
}

export interface LeadTimeSuggestion extends LeadTimeMeasurement {
    fromWarehouseId: string;
    toWarehouseId: string | null;
}

export interface ChannelWarehouseOptions {
    internalWarehouses: { warehouseId: string; name: string }[];
    channelWarehouses: { warehouseId: string; name: string }[];
}

// ─── API functions ────────────────────────────────────────────────────────────

export const getDistributionChannels = async (): Promise<AvailableChannel[]> =>
    ReparoApi({ method: 'GET', url: '/distribution/channels', body: null });

export interface OpenPoCount {
    channelAbbreviationKey: string;
    openPoCount: number;
}

export const getOpenPoCounts = async (): Promise<OpenPoCount[]> =>
    ReparoApi({ method: 'GET', url: '/distribution/open-po-counts', body: null });

export const getChannelWarehouseOptions = async (
    channelKey: string,
): Promise<ChannelWarehouseOptions> =>
    ReparoApi({ method: 'GET', url: `/distribution/channels/${channelKey}/warehouses`, body: null });

export const getChannelConfig = async (channelKey: string): Promise<DistributionChannelConfig | null> =>
    ReparoApi({ method: 'GET', url: `/distribution/channels/${encodeURIComponent(channelKey)}/config`, body: null });

export const upsertDistributionChannelConfig = async (
    channelKey: string,
    body: {
        isAggregated: boolean;
        demandSource: DistributionDemandSource;
        /** Rate signal for PO-driven channels: FORECAST or PAST_SALES. Null clears it (push). */
        velocitySource?: DistributionDemandSource | null;
        targetDohDays?: number | null;
        milestone1Days?: number;
        milestone2Days?: number;
        drrLookbackDays?: number | null;
        tierTargetDohDays?: Record<string, number> | null;
        /** 0–1 fraction of fromWH stock that can be sent in one plan run. */
        maxOutboundUtilizationPct?: number | null;
        /** Horizon (days) of inbound POs added to fromWH available stock. 0 = on-hand only. */
        inboundInTransitDays?: number;
        /** SKUs with fromWH stock below this threshold are excluded. */
        minSourceStockUnits?: number | null;
        /** Soft reserve (units) kept back at each fromWH. Plan sends down to this level. */
        sourceReserveUnits?: number | null;
        /** Internal days-on-hand floor (days). Caps total units leaving the network per SKU. */
        minInternalDohDays?: number | null;
        /** Max units per outbound shipment. Null = no cap. */
        maxUnitsPerShipment?: number | null;
        /** Max org-unit measurement per shipment. Null = no cap. */
        maxMeasurementPerShipment?: number | null;
        /** Measurement unit for maxMeasurementPerShipment. */
        measurementUnit?: 'weight' | 'volume' | null;
    },
): Promise<DistributionChannelConfig> =>
    ReparoApi({
        method: 'PUT',
        url: `/distribution/channels/${channelKey}/config`,
        body: JSON.stringify(body),
    });

export const upsertDistributionEmailConfig = async (
    channelKey: string,
    body: {
        enabled: boolean;
        signature?: string | null;
        replyTo?: string | null;
        subjectTemplate?: string;
        introLine?: string;
        instructionLines?: string[];
        alwaysTo?: string[];
        alwaysCc?: string[];
        byDestination?: Record<string, { to: string[]; cc: string[] }>;
        bySourceWarehouse?: Record<string, { shortCode: string; extraCc: string[]; to?: string[] }>;
    },
): Promise<DistributionChannelConfig> =>
    ReparoApi({
        method: 'PUT',
        url: `/distribution/channels/${encodeURIComponent(channelKey)}/email-config`,
        body: JSON.stringify(body),
    });

export const listWarehouseDohRules = async (channelKey: string): Promise<WarehouseDohRule[]> =>
    ReparoApi({ method: 'GET', url: `/distribution/channels/${channelKey}/config/warehouse-rules`, body: null });

export const upsertWarehouseDohRule = async (
    channelKey: string,
    warehouseId: string,
    body: { targetDohDays?: number | null; tierTargetDohDays?: Record<string, number> | null },
): Promise<WarehouseDohRule> =>
    ReparoApi({
        method: 'PUT',
        url: `/distribution/channels/${channelKey}/config/warehouse-rules/${warehouseId}`,
        body: JSON.stringify(body),
    });

export const deleteWarehouseDohRule = async (channelKey: string, warehouseId: string): Promise<void> =>
    ReparoApi({ method: 'DELETE', url: `/distribution/channels/${channelKey}/config/warehouse-rules/${warehouseId}`, body: null });

export const getDistributionPaths = async (channelKey: string): Promise<DistributionPath[]> =>
    ReparoApi({ method: 'GET', url: `/distribution/channels/${channelKey}/paths`, body: null });

export const upsertDistributionPaths = async (
    channelKey: string,
    paths: Array<{
        fromWarehouseId: string;
        toWarehouseId: string | null;
        leadTimeDays: number;
        leadTimeMode?: DistributionLeadTimeMode;
        isActive?: boolean;
    }>,
): Promise<DistributionPath[]> =>
    ReparoApi({
        method: 'PUT',
        url: `/distribution/channels/${channelKey}/paths`,
        body: JSON.stringify({ paths }),
    });

export const getLeadTimeSuggestions = async (
    channelKey: string,
    paths: Array<{ fromWarehouseId: string; toWarehouseId: string | null }>,
    windowDays: number,
): Promise<{ windowDays: number; channel: LeadTimeMeasurement; suggestions: LeadTimeSuggestion[] }> =>
    ReparoApi({
        method: 'POST',
        url: `/distribution/channels/${channelKey}/lead-time-suggestions`,
        body: JSON.stringify({ paths, windowDays }),
    });

export const updateDistributionPath = async (
    pathId: string,
    body: { leadTimeDays?: number; isActive?: boolean },
): Promise<DistributionPath> =>
    ReparoApi({
        method: 'PATCH',
        url: `/distribution/paths/${pathId}`,
        body: JSON.stringify(body),
    });

// ─── Overview types ───────────────────────────────────────────────────────────

export type DistributionHealthStatus = 'CRITICAL' | 'WARNING' | 'HEALTHY';
export type DistributionSeverity = 'HIGH' | 'MEDIUM' | 'LOW';
export type DistributionPriority = 'CRITICAL' | 'MEDIUM' | 'LOW';
export type DistributionTrigger = 'OPEN_PO' | 'SCHEDULED' | 'LOW_STOCK_ALERT';
export type DistributionPlanStatus = 'DRAFT' | 'APPROVED' | 'EXECUTING' | 'COMPLETED' | 'ARCHIVED';

export interface DistributionSkuHealth {
    productId: string;
    productName: string;
    channelStock: number;
    /**
     * Past-sales daily run rate — sales over the lookback window ÷ that SKU's sellable days on
     * this channel (stockout-adjusted when the org opted in, else clamped to its first sale).
     */
    currentDrr: number;
    /**
     * The rate currentDohDays divides by under the requested basis: currentDrr when BACKWARD,
     * the plan-shaped forecast rate when FORWARD.
     */
    effectiveDailyRate: number;
    currentDohDays: number;
    targetDohDays: number;
    /** Forecast demand for min-lead window. Null when min=max lead or no forecast. */
    forecastDemandLow: number | null;
    /** Forecast demand for max-lead window. Primary gap signal. Null when no forecast. */
    forecastDemandHigh: number | null;
    /** DRR-based demand: currentDrr × targetDohDays */
    drrDemand: number;
    /** Open-PO demand for the scoped PO (units on the PO). Null = not on the PO / no PO scope. */
    openPoDemand: number | null;
    gapUnits: number;
    /** On-hand + network incoming (in-transit + in-production) into internal WHs. */
    internalAvailableUnits: number;
    /** Split of the network incoming credited into internalAvailableUnits. */
    internalInTransit?: number;
    internalInProduction?: number;
    internalIncoming?: number;
    inTransit: number;
    /** Relative priority within this channel by gap. Null when no demand signal. */
    priority: DistributionPriority | null;
    category: string | null;
    productGroup: string | null;
    tier: string | null;
    isActive: boolean | null;
}

export interface DistributionChannelHealth {
    channelId: string;
    channelName: string;
    channelAbbreviationKey: string;
    /** SKUs with currentDohDays < targetDohDays */
    skuCount: number;
    /** All active SKUs in this channel */
    totalSkuCount: number;
    totalChannelStock: number;
    /** Sum of past-sales DRR across all SKUs. */
    channelDrr: number;
    /** Sum of effectiveDailyRate across all SKUs — the denominator of weightedDohDays. */
    channelRate: number;
    /** totalChannelStock / channelRate */
    weightedDohDays: number;
    targetDohDays: number;
    gapUnits: number;
    /** Sum of internalAvailableUnits across all SKUs */
    internalAvailableUnits: number;
    isAggregated: boolean;
    status: DistributionHealthStatus;
    severity: DistributionSeverity;
    minProcessDays: number;
    maxProcessDays: number;
    hasForecast: boolean;
    /** Effective DRR lookback window for this channel (request override, else org default). */
    drrLookbackDays: number;
    /** Which rate drove weightedDohDays and each SKU's effectiveDailyRate. */
    rateBasis: RateBasis;
    /** All SKUs in this channel's active universe */
    skus: DistributionSkuHealth[];
}

export interface DistributionOverviewData {
    channelsBelowTarget: number;
    totalChannels: number;
    totalGapUnits: number;
    totalGapSkus: number;
    internalAvailableUnits: number;
    channels: DistributionChannelHealth[];
    computedAt: string;
    /** Rate policy these numbers were computed with. */
    rateBasis?: RateBasis;
    drrWindowDays?: number;
    /** The org's saved defaults, so the rate control can mark an override as a preview. */
    savedRateBasis?: RateBasis;
    savedDrrWindowDays?: number;
    /** Whether confirmed out-of-stock days were excluded from the DRR denominator. */
    excludeStockoutDays?: boolean;
}

export interface DistributionPlanSummary {
    id: string;
    name: string;
    planDate: string;
    status: DistributionPlanStatus;
    triggerType: DistributionTrigger;
    channelAbbreviationKey: string;
    channelNames: string[];
    saleOrderCount: number;
    lineCount: number;
    /** Sum of approved (or recommended) units across non-excluded transfer lines. */
    totalUnits: number;
    /** Distinct products with an approved/recommended quantity > 0. */
    totalSkuQty: number;
    /** External sale-order codes (WMS-created) linked to this plan. */
    saleOrderCodes: string[];
}

/** Cross-channel Track command-centre counts over approved plans. */
export interface DistributionTrackOverview {
    notPlaced: number;
    awaitingDispatch: number;
    shipsToday: number;
    overdue: number;
    computedAt: string;
}

// ─── Channel health (unified) ─────────────────────────────────────────────────

export interface GetChannelHealthBody {
    channelKey?: string;
    warehouseId?: string;
    bust?: boolean;
    /** Plan-level flat DOH override — overrides channel config when set. */
    targetDohDays?: number;
    /** Plan-level per-tier DOH map — overrides channel config tier DOH when set. */
    tierTargetDohDays?: Record<string, number> | null;
    /** Plan-level Milestone 1 days override. */
    milestone1Days?: number;
    /** Plan-level Milestone 2 days override. */
    milestone2Days?: number;
    /** DRR lookback window (days) override. Falls back to the org's salesLookbackDays. */
    drrLookbackDays?: number;
    /** Plan-level explicit DRR window (yyyy-MM-dd, inclusive). Replaces the rolling lookback when both set. */
    drrWindowStart?: string | null;
    drrWindowEnd?: string | null;
    /** Plan-level growth % layered on the DRR: (1 + pct/100) × rate. */
    drrGrowthPct?: number | null;
    /**
     * Rate basis for the displayed DRR/DoH: FORWARD = forecast, BACKWARD = past sales.
     * Falls back to the org's saved lookup. Display only — plan compute is unaffected.
     */
    basis?: RateBasis;
    /** OPEN_PO plans: scope demand to this PO so available rows carry PO demand + membership. */
    purchaseOrderId?: string;
}

export const getChannelHealth = async (body: GetChannelHealthBody = {}): Promise<DistributionOverviewData> =>
    ReparoApi({ method: 'POST', url: '/distribution/channel-health', body: JSON.stringify(body) });

export const getDistributionPlanList = async (
    limit = 5,
    statuses?: DistributionPlanStatus[],
): Promise<DistributionPlanSummary[]> => {
    const qs = new URLSearchParams({ limit: String(limit) });
    if (statuses?.length) qs.set('status', statuses.join(','));
    return ReparoApi({ method: 'GET', url: `/distribution/plans?${qs.toString()}`, body: null });
};

export const getDistributionTrackOverview = async (): Promise<DistributionTrackOverview> =>
    ReparoApi({ method: 'GET', url: '/distribution/track/overview', body: null });

/** Derived lifecycle of one sale order (shipment code) in the Track table. */
export type DistributionTrackSaleOrderStatus =
    | 'NOT_PLACED'
    | 'AWAITING_DISPATCH'
    | 'DISPATCHED'
    | 'DEAD'
    | 'FAILED';

/** One Track row — a single sale order (shipment code), not a plan. */
export interface DistributionTrackSaleOrder {
    saleOrderCode: string;
    planId: string;
    planName: string;
    channelAbbreviationKey: string;
    status: DistributionTrackSaleOrderStatus;
    saleOrderStatus: string | null;
    wmsExternalId: string | null;
    saleOrderCreatedAt: string | null;
    skuCount: number;
    unitsApproved: number;
    unitsDispatched: number;
}

export const getDistributionTrackSaleOrders = async (filters: {
    channelKey?: string;
    statuses?: DistributionTrackSaleOrderStatus[];
    /** Matches a SKU code on any line of the sale order, or the sale order code. */
    search?: string;
} = {}): Promise<DistributionTrackSaleOrder[]> => {
    const qs = new URLSearchParams();
    if (filters.channelKey) qs.set('channelKey', filters.channelKey);
    if (filters.statuses?.length) qs.set('status', filters.statuses.join(','));
    if (filters.search?.trim()) qs.set('search', filters.search.trim());
    const suffix = qs.toString() ? `?${qs.toString()}` : '';
    return ReparoApi({ method: 'GET', url: `/distribution/track/sale-orders${suffix}`, body: null });
};


// ─── Plan types ───────────────────────────────────────────────────────────────

export type DistributionTransferStatus = 'PENDING' | 'APPROVED' | 'REJECTED';

export interface DistributionTransferRow {
    id: string;
    planId: string;
    pathId: string | null;
    channelAbbreviationKey: string;
    fromWarehouseId: string;
    fromWarehouseName: string | null;
    toWarehouseId: string | null;
    toWarehouseName: string | null;
    productId: string;
    productName: string | null;
    recommendedQty: number;
    approvedQty: number | null;
    status: DistributionTransferStatus;
    purchaseOrderId: string | null;
    category: string | null;
    productGroup: string | null;
    tier: string | null;
    isActive: boolean | null;
    inTransit: number;
    /** Current destination/channel stock at compute time (frozen). Null on older plans. */
    channelStock: number | null;
    /** Inbound in-transit across all internal WHs within the internal-DoH floor window at compute (frozen). Null on older plans. */
    networkInTransit: number | null;
    /** drr × processTimeDays — demand consumed while stock is in transit */
    leadTimeDemand: number | null;
    /**
     * Daily consumption rate at the destination under the plan's velocity signal, frozen at compute:
     * past-sales → the DRR run-rate; forecast → forecast ÷ target-DoH window; max → the larger.
     * The divisor behind every DoH figure on the line.
     */
    dailyDemand: number | null;
    /** Stock at the specific fromWarehouseId at plan-compute time. Null for aggregated plans. */
    fromWarehouseStock: number | null;
    /** productDoh × drr — DRR-based demand used in plan computation */
    drrDemand: number | null;
    /** Prorated forecast demand used in plan computation. Null for PAST_SALES / OPEN_PO demand sources. */
    forecastDemand: number | null;
    /** Raw demand to be met before netting (the PO qty for OPEN_PO plans). Null on legacy/non-PO lines. */
    targetDemand: number | null;
    /** Shipment group code stamped at approval time (RPR-xxx-N) */
    saleOrderCode: string | null;
    /** User-defined shipment label set at approval time. */
    saleOrderName: string | null;
    /** WMS push status. null = not pushed. 'CREATED' = in WMS. 'FAILED' = rejected. */
    wmsStatus: 'CREATED' | 'FAILED' | null;
    /** Unicommerce's returned sale order ID. */
    wmsExternalId: string | null;
    /** Error message when wmsStatus = 'FAILED'. */
    wmsErrorMessage: string | null;
    /** True when the From WH stock was below minSourceStockUnits at compute time — excluded from approval and SO creation. */
    isExcluded: boolean;
    /** Total plan qty for the destination × product before From WH splitting. Display metric only. */
    destinationPlanQty: number | null;
    /** Per-unit measurement value (weight_grams or volume_cbm) based on channel measurementUnit config. */
    measurementValue: number | null;
    /** Current DoH at the channel before this line is replenished. From frozen columns / live fallback; may be null. */
    currentDestinationDoh: number | null;
    /** Projected DoH at the channel once this line's qty is replenished. From frozen columns / live fallback; may be null. */
    destinationDohAfterReplenishment: number | null;
    /** Projected DoH across internal WHs vs other channels once this qty is earmarked. From frozen columns / live fallback; may be null. */
    internalDohAfterEarmark: number | null;
    /** AUTO = compute-produced; MANUAL = user-added (preserved across recompute). */
    origin?: 'AUTO' | 'MANUAL';
}

export interface DistributionPlanDetail {
    id: string;
    name: string;
    planDate: string;
    status: DistributionPlanStatus;
    channelAbbreviationKey: string;
    demandSource: string;
    targetDohDays: number;
    milestone1DaysOverride: number | null;
    milestone2DaysOverride: number | null;
    isAggregated: boolean;
    lineCount: number;
    saleOrderCount: number;
    /** ISO timestamp the plan was approved (numbers on an approved plan are frozen as of this). Null while DRAFT. */
    approvedAt: string | null;
    aiNarrative: string | null;
    aiNarrativeComputedAt: string | null;
    transfers: DistributionTransferRow[];
    warehouseCapacities: Record<string, { maxCapacity: number | null }>;
    /** Last-7-days sold units per destination warehouse, keyed by toWarehouseId. Window ends the day before planDate. */
    warehouseSales7d: Record<string, { units: number }>;
    /** Per-store per-category capacity caps (retail). warehouseId -> { Retail Category bucket -> ideal qty }. */
    warehouseCategoryCapacities: Record<string, Record<string, number>>;
    /** product_id -> Retail Category bucket, for grouping the per-category capacity table. */
    productRetailCategory: Record<string, string>;
    /** Inclusive yyyy-MM-dd date window used for warehouseSales7d. */
    salesWindow: { start: string; end: string };
    /** Warehouse IDs this plan was scoped to. Null = all warehouses. */
    scopedWarehouseIds: string[] | null;
    /** Source (from) warehouse IDs this plan was scoped to (non-aggregated). Null = all eligible sources. */
    scopedSourceWarehouseIds: string[] | null;
    /** Minimum From WH stock units required to create a transfer line. Null = no minimum. */
    minSourceStockUnits: number | null;
    /** Effective max units per outbound shipment. Null = no cap. */
    maxUnitsPerShipment: number | null;
    /** Effective max measurement per outbound shipment. Null = no cap. */
    maxMeasurementPerShipment: number | null;
    /** Current stock per source WH per product. whId → productId → qty. Used to show avail for WHs with no transfer entity. */
    fromWhStockMap: Record<string, Record<string, number>>;
    /** Inbound in-transit units per source WH per product over the channel's horizon. whId → productId → qty. Empty when inboundInTransitDays = 0. */
    fromWhInTransitMap: Record<string, Record<string, number>>;
    /** Names for ALL eligible source WHs (incl. zero-allocation ones), so every source path can be a column. */
    fromWhNames: Record<string, string>;
    /** Destination WH id (or 'null' for aggregated) → source WH ids with an active path to it. */
    destToSourceWhs: Record<string, string[]>;
    /** Per-tier DOH override used when this plan was computed. Null = channel config was used. */
    tierTargetDohDays: Record<string, number> | null;
    /** DRR lookback window (days) used when this plan was computed. Null = default (30) was used. */
    drrLookbackDays: number | null;
    /** Explicit DRR window (yyyy-MM-dd, inclusive) the plan's rate was summed over. Null = rolling lookback. */
    drrWindowStart: string | null;
    drrWindowEnd: string | null;
    /** Growth % layered on the DRR when this plan was computed. Null = none. */
    drrGrowthPct: number | null;
    /** maxOutboundUtilizationPct used when this plan was computed. Null = channel config was used. */
    maxOutboundUtilizationPct: number | null;
    /** inboundInTransitDays override used when this plan was computed. Null = channel config was used. */
    inboundInTransitDays: number | null;
    /** sourceReserveUnits override used when this plan was computed. Null = channel config was used. */
    sourceReserveUnits: number | null;
    /** minInternalDohDays override used when this plan was computed. Null = channel config was used. */
    minInternalDohDays: number | null;
    /** PO this plan was scoped to (OPEN_PO demand only). Null = whole-channel open POs. */
    sourcePurchaseOrderId: string | null;
}

/**
 * Plan settings an alert pins for its daily run — the plan builder's settings-page knobs. The
 * alert's deep link carries them (`ps=` JSON) so the plan it opens computes with the same set.
 */
export interface PlanSettingsOverride {
    demandSource?: string;
    targetDohDays?: number | null;
    tierTargetDohDays?: Record<string, number> | null;
    milestone1DaysOverride?: number | null;
    milestone2DaysOverride?: number | null;
    drrLookbackDays?: number | null;
    drrWindowStart?: string | null;
    drrWindowEnd?: string | null;
    drrGrowthPct?: number | null;
    minSourceStockUnitsOverride?: number | null;
    maxOutboundUtilizationPctOverride?: number | null;
    inboundInTransitDaysOverride?: number | null;
    sourceReserveUnitsOverride?: number | null;
    minInternalDohDaysOverride?: number | null;
    maxUnitsPerShipmentOverride?: number | null;
    maxMeasurementPerShipmentOverride?: number | null;
}

// ─── Plan API functions ───────────────────────────────────────────────────────

export const computeDistributionPlan = async (
    channelKey: string,
    body: {
        demandSource: string;
        /** When provided, overwrites this existing DRAFT plan instead of creating a new one. */
        existingPlanId?: string | null;
        /** PO-driven channels: generate the plan against this specific PO (demandSource = OPEN_PO). */
        purchaseOrderId?: string | null;
        /** Restrict the plan to these SKUs (e.g. the ones a channel-understock alert flagged). Omitted = whole channel. */
        productIds?: string[] | null;
        targetDohDays?: number | null;
        milestone1DaysOverride?: number | null;
        milestone2DaysOverride?: number | null;
        /** Lookback window in days for DRR calculation. Defaults to 30. */
        drrLookbackDays?: number | null;
        /** Explicit DRR window (yyyy-MM-dd, inclusive). When both set, replaces drrLookbackDays; divisor = days in window. */
        drrWindowStart?: string | null;
        drrWindowEnd?: string | null;
        /** Growth % layered on the DRR: effective rate = (1 + pct/100) × DRR. */
        drrGrowthPct?: number | null;
        tierTargetDohDays?: Record<string, number> | null;
        /** Non-aggregated channels only: restrict to these toWarehouseIds. Null/omitted = all. */
        warehouseIds?: string[] | null;
        /** Aggregated channels only: restrict to these path IDs (controls which source WHs participate). Null/omitted = all active paths. */
        pathIds?: string[] | null;
        /** Non-aggregated channels only: restrict to these source (from) warehouse IDs. Null/omitted = all eligible sources. */
        sourceWarehouseIds?: string[] | null;
        /** Per-warehouse flat DoH overrides for this compute run (non-aggregated only). */
        warehouseDohOverrides?: Record<string, number> | null;
        /** Per-warehouse per-tier DoH overrides for this compute run (non-aggregated only). warehouseId -> { tier -> days }. */
        warehouseTierDohOverrides?: Record<string, Record<string, number>> | null;
        /** Override channel-level minSourceStockUnits for this plan run only. Null = use channel config value. */
        minSourceStockUnitsOverride?: number | null;
        /** Override channel-level sourceReserveUnits for this plan run only. Null = use channel config value. */
        sourceReserveUnitsOverride?: number | null;
        /** Override channel-level minInternalDohDays for this plan run only. Null = use channel config value. */
        minInternalDohDaysOverride?: number | null;
        /** Override channel-level maxOutboundUtilizationPct for this plan run only (0–1). Null = use channel config value. */
        maxOutboundUtilizationPctOverride?: number | null;
        /** Override channel-level inboundInTransitDays for this plan run only. Null/0 = on-hand only for this run. */
        inboundInTransitDaysOverride?: number | null;
        /** Override channel-level maxUnitsPerShipment for this plan run only. Null = use channel config value. */
        maxUnitsPerShipmentOverride?: number | null;
        /** Override channel-level maxMeasurementPerShipment for this plan run only. Null = use channel config value. */
        maxMeasurementPerShipmentOverride?: number | null;
    },
): Promise<DistributionPlanDetail> =>
    ReparoApi({
        method: 'POST',
        url: `/distribution/channels/${channelKey}/plans`,
        body: JSON.stringify(body),
    });

export const getDistributionPlan = async (planId: string): Promise<DistributionPlanDetail> =>
    ReparoApi({ method: 'GET', url: `/distribution/plans/${planId}`, body: null });

export const getWmsChannelNames = async (channelKey: string): Promise<{ wmsType: string | null; channelNames: string[] }> =>
    ReparoApi({ method: 'GET', url: `/distribution/channels/${encodeURIComponent(channelKey)}/wms-channel-names`, body: null });

export interface ChannelProductMapping {
    productId: string;
    /** The id this product is listed under on the channel. ' | '-joined when it maps to several. */
    marketplaceSkuId: string;
    /** Channel secondary id (e.g. Amazon ASIN), same ' | ' joining. Null when none is mapped. */
    secondaryId: string | null;
}

/**
 * One row per product. Omit `productIds` for every mapped product on the channel — preferred for
 * large sets, since each id costs ~35 chars of URL and a few thousand SKUs would overflow the
 * request-header limit.
 */
export const getChannelProductMappings = async (
    channelKey: string,
    productIds?: string[],
): Promise<ChannelProductMapping[]> => {
    // An explicit empty list means "no products". Serialising it would produce a bare '?', which the
    // server reads as "no filter" and answers with the entire channel — so short-circuit instead.
    if (productIds?.length === 0) return [];
    const query = productIds
        ? `?${productIds.map(id => `productIds=${encodeURIComponent(id)}`).join('&')}`
        : '';
    return ReparoApi({
        method: 'GET',
        url: `/distribution/channels/${encodeURIComponent(channelKey)}/product-mappings${query}`,
        body: null,
    });
};

export interface ChannelPastSalesRow {
    /** Destination warehouse (store). Null for aggregated channels — value is channel-level. */
    warehouseId: string | null;
    productId: string;
    /** Outgoing units over the lookback window. */
    qty: number;
}

/** Reference-only: outgoing units per (store, SKU) over the last N days. Default 7. */
export const getChannelPastSales = async (
    channelKey: string,
    lookbackDays: number,
): Promise<ChannelPastSalesRow[]> =>
    ReparoApi({
        method: 'GET',
        url: `/distribution/channels/${encodeURIComponent(channelKey)}/past-sales?lookbackDays=${lookbackDays}`,
        body: null,
    });

export const approveDistributionPlan = async (
    planId: string,
    options: { createSaleOrders?: boolean; unicommerceChannelName?: string; shipmentNames?: string[]; selectedTransferIds?: string[] } = {},
): Promise<{ status: string }> =>
    ReparoApi({
        method: 'PATCH',
        url: `/distribution/plans/${planId}/approve`,
        body: JSON.stringify(options),
    });

export interface PlanShipmentLine {
    productId: string;
    productName: string | null;
    category: string | null;
    qty: number;
}

export interface PlanShipment {
    saleOrderCode: string;
    saleOrderName: string | null;
    wmsStatus: 'CREATED' | 'FAILED' | null;
    /** Live sale-order status from the WMS-synced OUTGOING PO (e.g. Shipped, Cancelled). Null until synced. */
    saleOrderStatus: string | null;
    /** Whether the sale order is still live (not Cancelled/Returned/Expired). Classified by the backend. */
    saleOrderIsLive: boolean;
    wmsExternalId: string | null;
    wmsErrorMessage: string | null;
    fromWarehouseId: string;
    fromWarehouseName: string | null;
    toWarehouseId: string | null;
    toWarehouseName: string | null;
    transferIds: string[];
    totalQty: number;
    /** Sum of qty x per-unit measurement (CBM when channel is volume-based). */
    totalMeasurement: number;
    lines: PlanShipmentLine[];
}

export const getPlanShipments = async (planId: string): Promise<PlanShipment[]> =>
    ReparoApi({ method: 'GET', url: `/distribution/plans/${planId}/sale-orders`, body: null });

export interface PlanEmailPreview {
    /** Shipments this draft covers. One entry per draft normally; every shipment when collapsed. */
    saleOrderCodes: string[];
    saleOrderCode: string;
    /** Comma-joined when the draft covers several shipments. */
    orderId: string | null;
    sourceWarehouseName: string | null;
    destinationWarehouseName: string | null;
    skuCount: number;
    qty: number;
    /** Formatted measurement, e.g. "3.80 CBM". Empty when the channel has no measurement unit. */
    measurement: string;
    subject: string;
    body: string;
    to: string[];
    cc: string[];
}

/**
 * Composed email drafts for an approved plan. `combine` collapses every shipment into a
 * single draft — recipients unioned, totals aggregated, one CSV attached per shipment.
 */
export const getPlanEmailPreview = async (
    planId: string,
    params: { dispatchDate?: string; receivingDate?: string; combine?: boolean } = {},
): Promise<PlanEmailPreview[]> => {
    const qs = new URLSearchParams();
    if (params.dispatchDate) qs.set('dispatchDate', params.dispatchDate);
    if (params.receivingDate) qs.set('receivingDate', params.receivingDate);
    if (params.combine) qs.set('combine', 'true');
    const suffix = qs.toString() ? `?${qs.toString()}` : '';
    return ReparoApi({ method: 'GET', url: `/distribution/plans/${planId}/email-preview${suffix}`, body: null });
};

export const sendPlanEmail = async (
    planId: string,
    body: { saleOrderCodes: string[]; to: string[]; cc?: string[]; subject: string; body: string },
): Promise<{ recipients: number }> =>
    ReparoApi({
        method: 'POST',
        url: `/distribution/plans/${planId}/email`,
        body: JSON.stringify(body),
    });

export interface SaleOrderCapacityLine {
    transferId: string;
    productId: string;
    productName: string | null;
    fromWarehouseId: string;
    fromWarehouseName: string | null;
    toWarehouseId: string | null;
    toWarehouseName: string | null;
    /** Quantity on the line — what the push sends, unchanged. */
    currentQty: number;
    /** What the source can send right now (current stock under the utilisation cap + reserve). */
    sendableQty: number;
    /** True when currentQty > sendableQty — flagged for the operator, pushed as-is. */
    exceedsAvailable: boolean;
    fromWhStock: number;
    maxUtilPct: number | null;
}

export interface SaleOrderCapacityPreview {
    lines: SaleOrderCapacityLine[];
    overAvailableLineCount: number;
    maxUtilPct: number | null;
}

export const previewSaleOrderCreation = async (planId: string): Promise<SaleOrderCapacityPreview> =>
    ReparoApi({ method: 'GET', url: `/distribution/plans/${planId}/sale-orders/preview`, body: null });

export const createSaleOrdersForApprovedPlan = async (
    planId: string,
    options: { unicommerceChannelName?: string } = {},
): Promise<void> =>
    ReparoApi({
        method: 'POST',
        url: `/distribution/plans/${planId}/sale-orders`,
        body: JSON.stringify(options),
    });

export const deleteDistributionTransfer = async (transferId: string): Promise<void> =>
    ReparoApi({ method: 'DELETE', url: `/distribution/transfers/${transferId}`, body: null });

export const getUnplannedSkus = async (
    planId: string,
    params: { fromWarehouseId?: string; toWarehouseId?: string } = {},
): Promise<DistributionSkuHealth[]> => {
    const query = new URLSearchParams();
    if (params.fromWarehouseId) query.set('fromWarehouseId', params.fromWarehouseId);
    if (params.toWarehouseId !== undefined) query.set('toWarehouseId', params.toWarehouseId);
    const qs = query.toString();
    return ReparoApi({
        method: 'GET',
        url: `/distribution/plans/${planId}/unplanned-skus${qs ? `?${qs}` : ''}`,
        body: null,
    });
};

export const deleteDistributionPlan = async (planId: string): Promise<void> =>
    ReparoApi({ method: 'DELETE', url: `/distribution/plans/${planId}`, body: null });

export const renameDistributionPlan = async (planId: string, name: string): Promise<void> =>
    ReparoApi({ method: 'PATCH', url: `/distribution/plans/${planId}`, body: JSON.stringify({ name }) });

export const updateDistributionTransfer = async (
    transferId: string,
    body: { approvedQty?: number; status?: DistributionTransferStatus; isExcluded?: boolean },
): Promise<DistributionTransferRow> =>
    ReparoApi({
        method: 'PATCH',
        url: `/distribution/transfers/${transferId}`,
        body: JSON.stringify(body),
    });

export const getDistributionWarehouseStock = async (
    warehouseIds: string[],
): Promise<{ productId: string; stock: number }[]> => {
    const params = new URLSearchParams();
    for (const id of warehouseIds) params.append('warehouseIds', id);
    return ReparoApi<{ productId: string; stock: number }[]>({
        method: 'GET',
        url: `/distribution/warehouse-sku-stock?${params.toString()}`,
    });
};

// ─── Preview (read-only what-if) ──────────────────────────────────────────────

export interface PreviewTransferLine {
    productId: string;
    toWarehouseId?: string | null;
    fromWarehouseId?: string | null;
    /** User-typed quantity to evaluate. Wins over targetDohDays. */
    qty?: number;
    /** User-typed channel target DoH — recomputes the suggested quantity. */
    targetDohDays?: number;
}

export interface PreviewTransferResult {
    productId: string;
    toWarehouseId: string | null;
    fromWarehouseId: string | null;
    recommendedQty: number;
    qty: number;
    isExcluded: boolean;
    /** Engine split result for a DoH-mode edit: source-WH id → allocated qty. Empty in qty-mode. */
    allocationsByFromWh: Record<string, number>;
    /** Effective (sendable, maxUtil-capped) stock per source WH — the true per-slot ceiling. */
    availableByFromWh: Record<string, number>;
    /** Total sendable units for this line (Σ availableByFromWh) — hard ceiling on qty. */
    maxSendable: number;
    /** Highest DoH the sendable stock can reach. Null when daily demand is 0. */
    maxReachableDoh: number | null;
    /** DoH-mode only: true if the requested target DoH was reachable within the sendable stock. */
    targetMet: boolean;
    /** Reason the target DoH couldn't be reached (null when met) — shown in the notification. */
    shortfallReason: string | null;
    /** DoH the channel has today for this SKU (current stock ÷ signal daily demand), before replenishment. */
    currentDestinationDoh: number | null;
    destinationDohAfterReplenishment: number | null;
    internalDohAfterEarmark: number | null;
}

/**
 * Read-only what-if. Recomputes suggested qty + both DoH projections for the given lines
 * without persisting. `body` carries the plan's demand signal + any plan-level overrides,
 * plus the `lines` to evaluate (each with an edited qty OR targetDohDays).
 */
export const previewDistributionTransfers = async (
    channelKey: string,
    planId: string,
    body: {
        demandSource: string;
        targetDohDays?: number | null;
        milestone1DaysOverride?: number | null;
        milestone2DaysOverride?: number | null;
        drrLookbackDays?: number | null;
        drrWindowStart?: string | null;
        drrWindowEnd?: string | null;
        drrGrowthPct?: number | null;
        minSourceStockUnitsOverride?: number | null;
        sourceReserveUnitsOverride?: number | null;
        minInternalDohDaysOverride?: number | null;
        // These MUST mirror the plan's compute settings so the what-if runs with identical
        // constraints (esp. the utilisation cap) — otherwise it over-allocates past what's sendable.
        maxOutboundUtilizationPctOverride?: number | null;
        inboundInTransitDaysOverride?: number | null;
        maxUnitsPerShipmentOverride?: number | null;
        maxMeasurementPerShipmentOverride?: number | null;
        tierTargetDohDays?: Record<string, number> | null;
        warehouseIds?: string[] | null;
        pathIds?: string[] | null;
        sourceWarehouseIds?: string[] | null;
        warehouseDohOverrides?: Record<string, number> | null;
        purchaseOrderId?: string | null;
        lines: PreviewTransferLine[];
    },
): Promise<PreviewTransferResult[]> =>
    ReparoApi({
        method: 'POST',
        url: `/distribution/channels/${encodeURIComponent(channelKey)}/plans/${planId}/preview-transfers`,
        body: JSON.stringify(body),
    });

export const addDistributionTransfer = async (
    planId: string,
    body: {
        productId: string;
        productName?: string | null;
        fromWarehouseId: string;
        toWarehouseId?: string | null;
        quantity: number;
    },
): Promise<DistributionTransferRow> =>
    ReparoApi({
        method: 'POST',
        url: `/distribution/plans/${planId}/transfers`,
        body: JSON.stringify(body),
    });

export interface SaveTransferEdit {
    productId: string;
    productName?: string | null;
    fromWarehouseId: string;
    toWarehouseId?: string | null;
    /** Final approved qty for this line. 0 zeroes an existing line; a new line at 0 is a no-op. */
    quantity: number;
}

/**
 * Atomically persist all inline plan edits in one transaction. Edits are addressed by natural key
 * (productId × fromWh × toWh), so transfer ids never matter — no stale-id 404s, no partial saves.
 * Returns the refreshed plan. Throws a 409 (statusCode 409) when the plan is no longer a DRAFT.
 */
export const saveDistributionTransfers = async (
    planId: string,
    edits: SaveTransferEdit[],
): Promise<DistributionPlanDetail> =>
    ReparoApi({
        method: 'PUT',
        url: `/distribution/plans/${planId}/transfers`,
        body: JSON.stringify({ edits }),
    });

// ─── WH Demand Overrides ──────────────────────────────────────────────────────

export interface WhDemandPeriod {
    periodStart: string;
    periodType: string;
    label: string;
}

export interface WhDemandRow {
    productId: string;
    sku: string;
    name: string;
    calculatedValues: Record<string, number>;
    overrideValues: Record<string, number | null>;
}

export interface WhDemandTableResponse {
    channelId: string;
    warehouseId: string;
    warehouseName: string;
    periodType: string;
    periods: WhDemandPeriod[];
    rows: WhDemandRow[];
}

export interface WhDemandOverrideRowInput {
    warehouseId: string;
    productId: string;
    periodStart: string;
    periodType: string;
    value: number;
}

export const getWhDemandTable = async (body: {
    channelId: string;
    warehouseId: string;
    startDate: string;
    endDate: string;
}): Promise<WhDemandTableResponse> =>
    ReparoApi({ method: 'POST', url: '/distribution/wh-demand/table', body: JSON.stringify(body) });

export const upsertWhDemandOverrides = async (body: {
    channelId: string;
    rows: WhDemandOverrideRowInput[];
}): Promise<{ success: boolean }> =>
    ReparoApi({ method: 'PUT', url: '/distribution/wh-demand', body: JSON.stringify(body) });

export const deleteWhDemandOverrides = async (body: {
    channelId: string;
    warehouseId?: string;
    productId?: string;
    periodStart?: string;
}): Promise<{ success: boolean }> =>
    ReparoApi({ method: 'DELETE', url: '/distribution/wh-demand', body: JSON.stringify(body) });
